import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { invalidateFor } from "@/lib/cache-registry";
import type { DBCustomer } from "@/lib/db-types";

// Kanonisk customers-rad-type bor i db-types.ts (DBCustomer). Beholder
// Customer som alias for bakoverkompatibilitet med eksisterende importer.
export type Customer = DBCustomer;

// CRM-1.4: status_override_* er lokale kolonner (ikke PO-felt) som kundelisten
// og kundekortet trenger for å vise "Overstyrt". logo_url (kundekort v3) vises
// i kundeliste v2 (VIB-2891).
const CUSTOMER_LIST_COLUMNS =
  "id, name, organization_number, contact_person, contact_email, invoice_email_address, invoice_email_cc, contact_phone, poweroffice_customer_id, country_code, status_override, status_override_reason, status_override_until, status_override_by, status_override_at, logo_url";

/**
 * Hele kunderegisteret, paginert. Rå `.select()` kuttes stille ved 1000 rader
 * (768 kunder i okt. 2026), og duplikatsjekken i `NewCustomerDialog` må se alle.
 * `id` som sekundær sortering gir stabile sidegrenser når navn er like.
 */
function fetchAllCustomers(): Promise<Customer[]> {
  return fetchAllPaginated<Customer>("customers", CUSTOMER_LIST_COLUMNS, undefined, ["name", "id"]);
}

export function useCustomers() {
  return useQuery({
    queryKey: ["customers"],
    queryFn: fetchAllCustomers,
    staleTime: 5 * 60 * 1000,
  });
}

export interface CustomerWithProjectCount extends DBCustomer {
  project_count: number;
}

/**
 * VIB-1290: kundeliste med prosjektantall per kunde (/kunder). Teller
 * ikke-slettede prosjekter klient-side. Prosjekt-lesingen går via
 * fetchAllPaginated selv om `projects` er under 1000-rads-taket i dag —
 * det er en team-bred, ubundet lesing, og paginering hindrer stille
 * under-telling om tabellen vokser forbi taket (jf. VIB-1249-fella).
 */
export function useCustomersWithProjectCounts() {
  return useQuery({
    queryKey: ["customers", "with-project-counts"],
    queryFn: async (): Promise<CustomerWithProjectCount[]> => {
      const [customers, projects] = await Promise.all([
        fetchAllCustomers(),
        fetchAllPaginated<{ id: string; customer_id: string | null }>(
          "projects",
          "id, customer_id",
          (q) => q.is("deleted_at", null),
        ),
      ]);

      const counts = new Map<string, number>();
      for (const p of projects) {
        if (p.customer_id) counts.set(p.customer_id, (counts.get(p.customer_id) ?? 0) + 1);
      }

      return customers.map((c) => ({ ...c, project_count: counts.get(c.id) ?? 0 }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

export type CustomerPatchableField =
  | "contact_person"
  | "contact_email"
  | "invoice_email_address"
  | "invoice_email_cc"
  | "contact_phone"
  | "address"
  | "postal_code"
  | "city";

/**
 * VIB-2531: kundeinfo lagres nå write-through via `update-po-customer`, ikke
 * direkte til Supabase.
 *
 * Hooken skrev tidligere rett til `customers`. `import-customers` (pg_cron
 * 04:30) gjør full upsert fra PowerOffice, så endringen ble stille overskrevet
 * neste morgen — en PL rettet kundeinfo kl 14, det så ut til å lykkes, og var
 * borte dagen etter.
 *
 * Edge function-en sender endringen til PO FØRST og skriver lokalt kun hvis PO
 * tok imot. Feiler PO, lagres ingenting og feilen vises. Da kan Supabase aldri
 * ha en verdi PO ikke kjenner, og nattsyncen blir harmløs av seg selv.
 *
 * NB: `contact_person` har ingen motsvarighet på PO sin Customer-entitet i v2,
 * og lagres kun lokalt. Det er trygt fordi VIB-2521 tok feltet ut av
 * sync-mappingen.
 *
 * Autorisasjon ligger i edge function-en (alle aktive ansatte, `canEditCustomers`),
 * og speiler RLS-policyen `customers_employee_update`. Den kan ikke håndheves av
 * RLS her, siden edge functions kjører som service_role og er BYPASSRLS.
 */
export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; field: CustomerPatchableField; value: string | null }) => {
      const { data, error } = await supabase.functions.invoke<{ ok: boolean; error?: string }>(
        "update-po-customer",
        { body: { customer_id: input.id, changes: { [input.field]: input.value } } },
      );
      // Edge function-en svarer med ikke-2xx ved avvist endring; supabase-js
      // pakker det som `error`, men detaljene ligger i responsbodyen.
      if (error) throw new Error(data?.error ?? error.message);
      if (!data?.ok) throw new Error(data?.error ?? "Kunne ikke lagre kundeinfo");
      return data;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "customers", { customerId: vars.id });
    },
    onError: (err) => {
      toast.error(`Kunne ikke oppdatere kunde: ${(err as Error).message}`);
    },
  });
}
